import React from 'react';
import {
  Box,
  Typography,
  useTheme,
  alpha,
} from '@mui/material';
import { SxProps, Theme } from '@mui/material/styles';

interface IOSStyleButtonProps {
  icon: React.ReactNode;
  label: string;
  onClick?: () => void;
  selected?: boolean;
  size?: 'small' | 'medium' | 'large' | 'unified';
  backgroundColor?: string;
  disabled?: boolean;
  sx?: SxProps<Theme>;
  className?: string;
}

const IOSStyleButton: React.FC<IOSStyleButtonProps> = ({
  icon,
  label,
  onClick,
  selected = false,
  size = 'medium',
  backgroundColor,
  disabled = false,
  sx = {},
  className = '',
}) => {
  const theme = useTheme();

  // 尺寸配置
  const sizeConfig = {
    small: {
      iconSize: 40,
      iconFontSize: '1.2rem',
      labelFontSize: '0.6rem',
      spacing: 4,
    },
    medium: {
      iconSize: 50,
      iconFontSize: '1.5rem',
      labelFontSize: '0.65rem',
      spacing: 6,
    },
    large: {
      iconSize: 60,
      iconFontSize: '1.8rem',
      labelFontSize: '0.7rem',
      spacing: 8,
    },
    // 新增：匹配顶部导航栏的统一尺寸
    unified: {
      iconSize: 32, // 与顶部导航栏保持一致 (40 * 0.8 = 32)
      iconFontSize: '1rem',
      labelFontSize: '0.55rem',
      spacing: 3,
    },
  };

  const config = sizeConfig[size];

  // 默认背景色 - 为不同图标提供不同的背景色
  const getDefaultBackgroundColor = () => {
    const colors = [
      '#007AFF', // 蓝色
      '#FF9500', // 橙色
      '#34C759', // 绿色
      '#FF3B30', // 红色
      '#AF52DE', // 紫色
      '#FF2D92', // 粉色
      '#5AC8FA', // 浅蓝色
      '#FFCC00', // 黄色
      '#FF6B6B', // 浅红色
      '#4ECDC4', // 青色
      '#45B7D1', // 天蓝色
      '#96CEB4', // 薄荷绿
    ];
    
    // 根据label的hash值选择颜色
    let hash = 0;
    for (let i = 0; i < label.length; i++) {
      hash = ((hash << 5) - hash + label.charCodeAt(i)) & 0xffffffff;
    }
    return colors[Math.abs(hash) % colors.length];
  };

  const bgColor = backgroundColor || getDefaultBackgroundColor();

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        cursor: disabled ? 'default' : 'pointer',
        opacity: disabled ? 0.5 : 1,
        transition: 'all 0.2s ease-in-out',
        '&:hover': !disabled ? {
          transform: 'scale(1.05)',
        } : {},
        '&:active': !disabled ? {
          transform: 'scale(0.95)',
        } : {},
        ...sx,
      }}
      onClick={disabled ? undefined : onClick}
      className={`ios-style-button ${className}`}
    >
      {/* 图标容器 */}
      <Box
        sx={{
          width: config.iconSize,
          height: config.iconSize,
          borderRadius: 2.5, // iOS风格的圆角
          background: selected 
            ? `linear-gradient(135deg, ${bgColor}, ${alpha(bgColor, 0.8)})` 
            : `linear-gradient(135deg, ${bgColor}, ${alpha(bgColor, 0.9)})`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          boxShadow: selected 
            ? `0 4px 12px ${alpha(bgColor, 0.4)}` 
            : `0 2px 8px ${alpha(bgColor, 0.3)}`,
          border: selected ? `2px solid ${theme.palette.primary.main}` : 'none',
          transition: 'all 0.2s ease-in-out',
          position: 'relative',
          '&::before': {
            content: '""',
            position: 'absolute',
            top: '10%',
            left: '10%',
            right: '10%',
            height: '30%',
            background: 'linear-gradient(to bottom, rgba(255,255,255,0.3), rgba(255,255,255,0))',
            borderRadius: '50%',
            filter: 'blur(1px)',
          },
          '& .MuiSvgIcon-root': {
            fontSize: config.iconFontSize,
            color: '#FFFFFF',
            filter: 'drop-shadow(0 1px 2px rgba(0,0,0,0.2))',
          },
        }}
      >
        {icon}
      </Box>

      {/* 标签文字 */}
      <Typography
        variant="caption"
        sx={{
          fontSize: config.labelFontSize,
          color: selected ? theme.palette.primary.main : theme.palette.text.primary,
          fontWeight: selected ? 600 : 500,
          marginTop: config.spacing / 8,
          textAlign: 'center',
          lineHeight: 1.2,
          maxWidth: config.iconSize + 10,
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
          textShadow: selected ? `0 0 4px ${alpha(theme.palette.primary.main, 0.3)}` : 'none',
        }}
      >
        {label}
      </Typography>
    </Box>
  );
};

export default IOSStyleButton; 